import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Award,
  Trophy,
  BarChart3,
  ChevronDown,
  Download,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Contact2,
  FileDown,
  FolderInput,
  FolderKanban,
  Home,
  Lock,
  LogOut,
  Menu,
  MessageSquare,
  Leaf,
  Moon,
  Newspaper,
  Package,
  Plug,
  Server,
  Settings,
  Bell,
  BellOff,
  ShieldQuestion,
  Sun,
  Target,
  TrendingUp,
  User,
  Users,
} from 'lucide-react';
import logoSymbol from './assets/logo-symbol.png';
import logoWordmark from './assets/logo-wordmark.png';
import logoWordmarkDark from './assets/logo-wordmark_dark.png';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from './components/ui/dialog';
import { Checkbox } from './components/ui/checkbox';
import { Switch } from './components/ui/switch';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { cn } from './lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from './components/ui/command';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from './components/ui/sheet';
import { NotificationBadge } from './components/NotificationBadge';
import { useNotifications } from './hooks/useNotifications';

// Import our new modules
import { ActiveView, Quote, StorageSection } from './types';
import { adminRole, editorRole, viewerRole, orcamentistaRole, medalhasRole, trofeusRole, tombstonesRole, placasRole, sdrRole, mediaRole, bloggerRole } from './constants/roles';
import { useAuth } from './hooks/useAuth';
import { useMaterials } from './hooks/useMaterials';
import { useUsers } from './hooks/useUsers';
import { useQuotes } from './hooks/useQuotes';
import { useSettings } from './hooks/useSettings';
import { useHsm } from './hooks/useHsm';
import { useBant } from './hooks/useBant';
import { usePushNotifications } from './hooks/usePushNotifications';
import { usePwaInstall } from './hooks/usePwaInstall';
import { MaterialsPage } from './components/pages/MaterialsPage';
import { HomePage } from './components/pages/HomePage';
import { LoginPage } from './components/pages/LoginPage';
import { QuotesPage } from './components/pages/QuotesPage';
import { SettingsPage } from './components/pages/SettingsPage';
import { UsersPage } from './components/pages/UsersPage';
import { UserSettingsPage } from './components/pages/UserSettingsPage';
import { HsmPage } from './components/pages/HsmPage';
import { BantPage } from './components/pages/BantPage';
import { ContactsPage } from './components/pages/ContactsPage';
import { StoragePage } from './components/pages/StoragePage';
import { AnalyticsPage } from './components/pages/AnalyticsPage';
import { MedalsPage } from './components/pages/MedalsPage';
import { TrophiesPage } from './components/pages/TrophiesPage';
import { TombstonesPage } from './components/pages/TombstonesPage';
import { PlacasPage } from './components/pages/PlacasPage';
import { RepescagemPage } from './components/pages/repescagem';
import { MonitorPage } from './components/pages/MonitorPage';
import { MetaTargetingPage } from './components/pages/MetaTargetingPage';
import { GoogleTrendsPage } from './components/pages/GoogleTrendsPage';
import { NginxAnalyticsPage } from './components/pages/NginxAnalyticsPage';
import { BlogPage } from './components/pages/BlogPage';
import { ContaAzulPage } from './components/pages/ContaAzulPage';
import { CreateQuoteDialog } from './components/CreateQuoteDialog';
import { useContacts } from './hooks/useContacts';
import { useMedals } from './hooks/useMedals';
import { useTrophies } from './hooks/useTrophies';
import { useTombstones } from './hooks/useTombstones';
import { usePlacas } from './hooks/usePlacas';
import { useNavigation } from './hooks/useNavigation';
import { useUtmAnalytics } from './hooks/useUtmAnalytics';
import { calculateDimensionBasedPriceBreakdown, normalizePhoneE164 } from './utils/pricing';
import { ToastProvider, useToast } from './contexts/ToastContext';

// Tombstone icon - monument tablet with star (matches NotificationBadge)
const TombstoneIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="5" y="2" width="14" height="17" rx="2.5"/>
    <path d="M8 22h8"/>
    <path d="M10 19v3"/>
    <path d="M14 19v3"/>
    <path d="M12 7.5l1.1 2.2 2.4.35-1.75 1.7.4 2.4L12 13l-2.15 1.15.4-2.4-1.75-1.7 2.4-.35Z" opacity="0.4"/>
  </svg>
);

// Placa icon - award plaque with ribbon (horizontal plaque shape)
const PlacaIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <path d="M8 12h8"/>
    <path d="M8 9h4"/>
    <path d="M8 15h6"/>
  </svg>
);

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

function AppContent() {
  // Theme state - default to dark mode
  const [theme, setTheme] = useState<'light' | 'dark' | 'moss'>(() => {
    const saved = localStorage.getItem('mt-backoffice:theme');
    return (saved === 'dark' || saved === 'light' || saved === 'moss') ? saved : 'dark';
  });

  // Toast context
  const toast = useToast();

  // Active view routing
  const [activeView, setActiveView] = useState<ActiveView>('home');
  const [storageSection, setStorageSection] = useState<StorageSection>('inventario');

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Sidebar drill-down state (null = main menu, string = section name)
  const [sidebarSection, setSidebarSection] = useState<string | null>(null);
  const [mobileSidebarSection, setMobileSidebarSection] = useState<string | null>(null);

  // Medalhas sub-tab state
  const [medalsTab, setMedalsTab] = useState<'products' | 'categories' | 'materials' | 'ribbons' | 'settings' | 'quotes'>('products');

  // Troféus sub-tab state
  const [trophiesTab, setTrophiesTab] = useState<'products' | 'quotes'>('products');

  // Tombstones sub-tab state
  const [tombstonesTab, setTombstonesTab] = useState<'products' | 'quotes'>('products');

  // Placas sub-tab state
  const [placasTab, setPlacasTab] = useState<'products' | 'quotes'>('products');

  // Analytics section state
  const [analyticsSection, setAnalyticsSection] = useState<'utm' | 'chapas' | 'medalhas'>('utm');

  // Register dialog state (for users view)
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);

  // Quote PDF download state
  const [quotePdfDownloading, setQuotePdfDownloading] = useState(false);

  // Quote status update state
  const [quoteStatusUpdating, setQuoteStatusUpdating] = useState(false);

  // Create quote dialog state
  const [createQuoteDialogOpen, setCreateQuoteDialogOpen] = useState(false);

  // Track which quotes have already had their notification counter decremented
  const viewedQuotesRef = useRef<Set<string>>(new Set());

  // Initialize auth hook
  const auth = useAuth();
  const { user, isAuthenticated, accessToken, refreshToken, attemptRefresh } = auth;

  // Derived permission checks
  const isLoggedIn = isAuthenticated;
  const isAdmin = Boolean(user?.roles.includes(adminRole));
  const canEdit = Boolean(user?.roles.some((role) => [editorRole, adminRole, orcamentistaRole].includes(role as typeof editorRole | typeof adminRole | typeof orcamentistaRole)));
  const canView = Boolean(user?.roles.some((role) => [viewerRole, editorRole, adminRole, orcamentistaRole].includes(role as typeof viewerRole | typeof editorRole | typeof adminRole | typeof orcamentistaRole)));
  const canManageQuotes = Boolean(user?.roles.some((role) => [adminRole, editorRole, orcamentistaRole].includes(role as typeof adminRole | typeof editorRole | typeof orcamentistaRole)));
  const canManageHsm = Boolean(user?.roles.some((role) => [adminRole, editorRole, orcamentistaRole].includes(role as typeof adminRole | typeof editorRole | typeof orcamentistaRole)));
  const canManageBant = Boolean(user?.roles.some((role) => [adminRole, editorRole, orcamentistaRole].includes(role as typeof adminRole | typeof editorRole | typeof orcamentistaRole)));
  const canManageContacts = Boolean(user?.roles.some((role) => [adminRole, editorRole, orcamentistaRole].includes(role as typeof adminRole | typeof editorRole | typeof orcamentistaRole)));
  const canManageStorage = Boolean(user?.roles.some((role) => [adminRole, 'STORAGE_MANAGER', 'STORAGE_AUDITOR'].includes(role)));
  const canViewAnalytics = Boolean(user?.roles.some((role) => [adminRole, editorRole].includes(role as typeof adminRole | typeof editorRole)));
  const canManageMedals = Boolean(user?.roles.some((role) => [adminRole, medalhasRole, orcamentistaRole].includes(role as typeof adminRole | typeof medalhasRole | typeof orcamentistaRole)));
  const canManageTrophies = Boolean(user?.roles.some((role) => [adminRole, trofeusRole, orcamentistaRole].includes(role as typeof adminRole | typeof trofeusRole | typeof orcamentistaRole)));
  const canManageTombstones = Boolean(user?.roles.some((role) => [adminRole, tombstonesRole, orcamentistaRole].includes(role as typeof adminRole | typeof tombstonesRole | typeof orcamentistaRole)));
  const canManagePlacas = Boolean(user?.roles.some((role) => [adminRole, placasRole, orcamentistaRole].includes(role as typeof adminRole | typeof placasRole | typeof orcamentistaRole)));
  const canManageRepescagem = Boolean(user?.roles.some((role) => [adminRole, sdrRole].includes(role as typeof adminRole | typeof sdrRole)));
  const canManageMetaTargeting = Boolean(user?.roles.some((role) => [adminRole, mediaRole].includes(role as typeof adminRole | typeof mediaRole)));
  const canManageGoogleTrends = Boolean(user?.roles.some((role) => [adminRole, mediaRole].includes(role as typeof adminRole | typeof mediaRole)));
  const canManageBlog = Boolean(user?.roles.some((role) => [adminRole, bloggerRole, mediaRole].includes(role as typeof adminRole | typeof bloggerRole | typeof mediaRole)));

  // Initialize domain hooks
  const materials = useMaterials({ accessToken, refreshToken, attemptRefresh });
  const users = useUsers({ accessToken, refreshToken, attemptRefresh, isAdmin });
  const quotes = useQuotes({ accessToken, refreshToken, attemptRefresh, canManageQuotes });
  const settings = useSettings({ accessToken, refreshToken, attemptRefresh, canEdit });
  const hsm = useHsm({ accessToken, refreshToken, attemptRefresh, canManageHsm });
  const bant = useBant({ accessToken, refreshToken, attemptRefresh, canManageBant });
  const contacts = useContacts({ accessToken, refreshToken, attemptRefresh, canManageContacts });
  const analytics = useUtmAnalytics({ accessToken, refreshToken, attemptRefresh, canViewAnalytics });
  const medals = useMedals({ accessToken, refreshToken, attemptRefresh });
  const trophies = useTrophies({ accessToken, refreshToken, attemptRefresh });
  const tombstones = useTombstones({ accessToken, refreshToken, attemptRefresh });
  const placas = usePlacas({ accessToken, refreshToken, attemptRefresh });

  // Stable callback references for SSE notifications
  // These need to be memoized to prevent stale closures when quotes object changes
  const handleNewQuoteSSE = useCallback(() => {
    // Delay refetch slightly — SSE fires before the creating transaction commits
    setTimeout(() => { void quotes.fetchQuotes(); }, 500);
  }, [quotes.fetchQuotes]);

  const handleQuoteViewedSSE = useCallback((quoteId: string) => {
    // Update quotes list when any user marks a quote as viewed (global counter)
    quotes.setQuotes((prev: Quote[]) =>
      prev.map((q: Quote) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );
  }, [quotes.setQuotes]);

  const handleQuoteDeletedSSE = useCallback(() => {
    setTimeout(() => { void quotes.fetchQuotes(); }, 500);
  }, [quotes.fetchQuotes]);

  // Medal quote SSE callbacks
  const handleNewMedalQuoteSSE = useCallback(() => {
    setTimeout(() => { void medals.fetchQuotes(); }, 500);
  }, [medals.fetchQuotes]);

  const handleMedalQuoteViewedSSE = useCallback((quoteId: string) => {
    // Update medal quotes list when any user marks a medal quote as viewed
    medals.setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );
  }, [medals.setQuotes]);

  // Trophy quote SSE callbacks
  const handleNewTrophyQuoteSSE = useCallback(() => {
    setTimeout(() => { void trophies.fetchQuotes(); }, 500);
  }, [trophies.fetchQuotes]);

  const handleTrophyQuoteViewedSSE = useCallback((quoteId: string) => {
    // Update trophy quotes list when any user marks a trophy quote as viewed
    trophies.setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );
  }, [trophies.setQuotes]);

  // Tombstone quote SSE callbacks
  const handleNewTombstoneQuoteSSE = useCallback(() => {
    setTimeout(() => { void tombstones.fetchQuotes(); }, 500);
  }, [tombstones.fetchQuotes]);

  const handleTombstoneQuoteViewedSSE = useCallback((quoteId: string) => {
    // Update tombstone quotes list when any user marks a tombstone quote as viewed
    tombstones.setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );
  }, [tombstones.setQuotes]);

  // Placa quote SSE callbacks
  const handleNewPlacaQuoteSSE = useCallback(() => {
    setTimeout(() => { void placas.fetchQuotes(); }, 500);
  }, [placas.fetchQuotes]);

  const handlePlacaQuoteViewedSSE = useCallback((quoteId: string) => {
    placas.setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );
  }, [placas.setQuotes]);

  // SSE callbacks for quote status/content changes (triggers list refetch)
  const handleQuoteChangedSSE = useCallback(() => { setTimeout(() => { void quotes.fetchQuotes(); }, 500); }, [quotes.fetchQuotes]);
  const handleMedalQuoteChangedSSE = useCallback(() => { setTimeout(() => { void medals.fetchQuotes(); }, 500); }, [medals.fetchQuotes]);
  const handleTrophyQuoteChangedSSE = useCallback(() => { setTimeout(() => { void trophies.fetchQuotes(); }, 500); }, [trophies.fetchQuotes]);
  const handleTombstoneQuoteChangedSSE = useCallback(() => { setTimeout(() => { void tombstones.fetchQuotes(); }, 500); }, [tombstones.fetchQuotes]);
  const handlePlacaQuoteChangedSSE = useCallback(() => { setTimeout(() => { void placas.fetchQuotes(); }, 500); }, [placas.fetchQuotes]);

  // Notification system hook - with stable callback references
  const notifications = useNotifications(
    accessToken || null,
    user?.roles || [],
    handleNewQuoteSSE,
    handleQuoteViewedSSE,
    canManageQuotes,
    handleQuoteDeletedSSE,
    canManageMedals,
    handleNewMedalQuoteSSE,
    handleMedalQuoteViewedSSE,
    canManageTrophies,
    handleNewTrophyQuoteSSE,
    handleTrophyQuoteViewedSSE,
    canManageTombstones,
    handleNewTombstoneQuoteSSE,
    handleTombstoneQuoteViewedSSE,
    handleQuoteChangedSSE,
    handleMedalQuoteChangedSSE,
    handleTrophyQuoteChangedSSE,
    handleTombstoneQuoteChangedSSE,
    canManagePlacas,
    handleNewPlacaQuoteSSE,
    handlePlacaQuoteViewedSSE,
    handlePlacaQuoteChangedSSE
  );

  // Theme effect
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark', 'moss');
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'moss') {
      // Moss is a dark theme variant — needs both classes so dark: utilities work
      root.classList.add('dark', 'moss');
    }
    localStorage.setItem('mt-backoffice:theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : prev === 'dark' ? 'moss' : 'light');
  }, []);

  const push = usePushNotifications();
  const pwa = usePwaInstall();

  // Data fetching effect based on active view
  useEffect(() => {
    if (!isLoggedIn) {
      setActiveView('home');
      return;
    }

    if (activeView === 'materials') {
      void materials.fetchMaterials().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }

    if (activeView === 'users' && isAdmin) {
      void users.fetchUsers().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void users.fetchRoles().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }

    if (activeView === 'settings' && canEdit) {
      void settings.fetchSettings().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void settings.fetchPricingSettings().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }

    if (activeView === 'quotes' && canManageQuotes) {
      void quotes.fetchQuotes().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void notifications.refreshUnreadCount();
    }

    if (activeView === 'hsm' && canManageHsm) {
      void hsm.fetchContacts().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void hsm.fetchCampaignMessages().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void hsm.fetchCampaignStats().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void hsm.fetchMessageLogs().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void hsm.fetchTemplates().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }

    if (activeView === 'bant' && canManageBant) {
      void bant.fetchQualifications().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }

    if (activeView === 'contacts' && canManageContacts) {
      void contacts.fetchContacts().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }

    if (activeView === 'analytics' && canViewAnalytics) {
      void analytics.fetchAnalytics().then(result => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      void analytics.fetchConversions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, activeView, isAdmin, canEdit, canManageQuotes, canManageHsm, canManageBant, canManageContacts, canViewAnalytics]);

  // Close register dialog when changing views
  useEffect(() => {
    if (activeView !== 'users') {
      setRegisterDialogOpen(false);
    }
  }, [activeView]);

  // Navigation handlers (extracted to useNavigation hook)
  const hsmCallbacks = useMemo(() => ({
    fetchContacts: hsm.fetchContacts,
    fetchCampaignMessages: hsm.fetchCampaignMessages,
    fetchCampaignStats: hsm.fetchCampaignStats,
    fetchMessageLogs: hsm.fetchMessageLogs,
    fetchTemplates: hsm.fetchTemplates,
  }), [hsm.fetchContacts, hsm.fetchCampaignMessages, hsm.fetchCampaignStats, hsm.fetchMessageLogs, hsm.fetchTemplates]);

  const bantCallbacks = useMemo(() => ({
    fetchQualifications: bant.fetchQualifications,
  }), [bant.fetchQualifications]);

  const contactsCallbacks = useMemo(() => ({
    fetchContacts: contacts.fetchContacts,
  }), [contacts.fetchContacts]);

  const analyticsCallbacks = useMemo(() => ({
    fetchAnalytics: analytics.fetchAnalytics,
  }), [analytics.fetchAnalytics]);

  // Storage callbacks will be provided by StoragePage itself
  // since it manages its own state

  const {
    navigateToHome,
    navigateToMaterials,
    navigateToUsers,
    navigateToSettings,
    navigateToQuotes,
    navigateToUserSettings,
    navigateToHsm,
    navigateToBant,
    navigateToContacts,
    navigateToStorage,
    navigateToStorageSection,
    navigateToAnalytics,
    navigateToAnalyticsSection,
    navigateToMedals,
    navigateToRepescagem,
    navigateToMetaTargeting,
    navigateToGoogleTrends,
  } = useNavigation({
    setActiveView,
    setStorageSection,
    setAnalyticsSection,
    canManageHsm,
    canManageBant,
    canManageContacts,
    canManageStorage,
    canViewAnalytics,
    hsmCallbacks,
    bantCallbacks,
    contactsCallbacks,
    analyticsCallbacks,
  });

  // Login handler
  const handleLogin = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      toast.clear();

      const result = await auth.login();
      if (result.success) {
        toast.success(result.message);
        setActiveView('home');
      } else {
        toast.error(result.message);
      }
    },
    [auth]
  );

  // Logout handler
  const handleLogout = useCallback(async () => {
    const message = await auth.logout();
    setActiveView('home');
    toast.success(message);
    // Clear the viewed quotes tracking on logout
    viewedQuotesRef.current.clear();
  }, [auth]);

  // Register handler
  const handleRegisterSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const result = await auth.register();
      if (result.success) {
        toast.success(result.message);
        await users.fetchUsers();
      }
    },
    [auth, users]
  );

  // Material handlers
  const handleCreateMaterial = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await materials.createMaterial();
      if (result.success) {
        toast.success('Material criado com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [materials]
  );

  const handleUpdateMaterial = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await materials.updateMaterial();
      if (result.success) {
        toast.success('Material atualizado com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [materials]
  );

  // User handlers
  const handleEditRolesSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await users.updateUserRoles();
      if (result.success) {
        toast.success('Papéis atualizados com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [users]
  );

  const handleResetPasswordSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await users.resetUserPassword();
      if (result.success) {
        toast.success('Senha redefinida com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [users]
  );

  const handleToggleUserStatus = useCallback(async () => {
    const result = await users.toggleUserStatus();
    if (result.success) {
      toast.success('Status do usuário alterado com sucesso.');
    } else {
      toast.error(result.error);
    }
  }, [users]);

  const handleDeleteUser = useCallback(async () => {
    const result = await users.deleteUser();
    if (result.success) {
      toast.success('Usuário deletado com sucesso.');
    } else {
      toast.error(result.error);
    }
  }, [users]);

  // Quote handlers
  const fetchQuotes = useCallback(async () => {
    const result = await quotes.fetchQuotes();
    if (!result.success && result.error) {
      toast.error(result.error);
    }
    return result;
  }, [quotes]);

  const handleOpenQuoteDialog = useCallback(
    async (quote: Quote) => {
      quotes.openQuoteDetails(quote);
      // Mark as viewed if not already (SSE will handle counter update globally)
      if (!quote.isViewed && !viewedQuotesRef.current.has(quote.id)) {
        // Track to prevent duplicate API calls in the same session
        viewedQuotesRef.current.add(quote.id);
        await quotes.markAsViewed(quote.id);
        // Note: Counter decrement handled by SSE broadcast
      }
    },
    [quotes]
  );

  const handleDownloadQuotePDF = useCallback(
    async (quoteId: string, quoteNumber: string) => {
      if (!accessToken) return;

      setQuotePdfDownloading(true);
      try {
        const quotesService = await import('./services/quotes.service');
        const blob = await quotesService.downloadQuotePDF(quoteId, accessToken);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orcamento-${quoteNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Erro ao baixar PDF');
      } finally {
        setQuotePdfDownloading(false);
      }
    },
    [accessToken]
  );

  const handleUpdateQuoteStatus = useCallback(
    async (quoteId: string, status: 'pending' | 'approved' | 'rejected') => {
      if (!accessToken) return;

      setQuoteStatusUpdating(true);
      try {
        const quotesService = await import('./services/quotes.service');
        await quotesService.updateQuoteStatus(quoteId, status, accessToken);

        // Update selectedQuote to reflect the new status immediately
        if (quotes.selectedQuote?.id === quoteId) {
          quotes.setSelectedQuote({ ...quotes.selectedQuote, status });
        }

        await fetchQuotes();
        toast.success('Status do orçamento atualizado com sucesso.');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Erro ao atualizar status');
      } finally {
        setQuoteStatusUpdating(false);
      }
    },
    [accessToken, fetchQuotes, quotes.selectedQuote, quotes.setSelectedQuote]
  );

  const handleSaveQuoteChanges = useCallback(
    async (payload: any) => {
      if (!accessToken || !quotes.selectedQuote) return;

      const quoteId = quotes.selectedQuote.id;
      const result = await quotes.updateQuote(quoteId, payload);

      if (result.success) {
        toast.success('Orçamento atualizado com sucesso!');

        // Refetch all quotes to get fresh data
        const fetchResult = await fetchQuotes();

        // Update selectedQuote with the refreshed data from the fetched list
        if (fetchResult.success && fetchResult.data) {
          const refreshedQuote = fetchResult.data.find(q => q.id === quoteId);
          if (refreshedQuote) {
            quotes.setSelectedQuote(refreshedQuote);
          }
        }
      } else {
        toast.error(result.error || 'Erro ao atualizar orçamento');
      }
    },
    [accessToken, quotes, fetchQuotes]
  );

  // Settings handlers
  const handleSaveMinimumOrder = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await settings.saveMinimumOrder();
      if (result.success) {
        toast.success('Configuração salva com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [settings]
  );

  const handleSavePricingSettings = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await settings.savePricingSettings();
      if (result.success) {
        toast.success('Configurações de precificação salvas com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [settings]
  );

  // Price breakdown calculation for settings page (using extracted utility)
  const calculatePriceBreakdown = useCallback(
    () => calculateDimensionBasedPriceBreakdown(
      settings.testCalc,
      settings.pricingSettings,
      settings.pricingInputs
    ),
    [settings.testCalc, settings.pricingInputs, settings.pricingSettings]
  );

  // HSM handlers (normalizePhoneE164 imported from utils/pricing)
  const handleCreateContact = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await hsm.createContact({
        phoneNumber: normalizePhoneE164(hsm.contactPhoneNumber),
        name: hsm.contactName || undefined,
        company: hsm.contactCompany || undefined,
        cpfCnpj: hsm.contactCpfCnpj || undefined,
        email: hsm.contactEmail || undefined,
        campaignDay: parseInt(hsm.contactCampaignDay) || 1
      });
      if (result.success) {
        toast.success('Contato criado com sucesso.');
        hsm.setContactDialogOpen(false);
        hsm.resetContactForm();
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleUpdateContact = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!hsm.selectedContact) return;
      const result = await hsm.updateContact(hsm.selectedContact.id, {
        phoneNumber: normalizePhoneE164(hsm.contactPhoneNumber),
        name: hsm.contactName || undefined,
        company: hsm.contactCompany || undefined,
        cpfCnpj: hsm.contactCpfCnpj || undefined,
        email: hsm.contactEmail || undefined,
        campaignDay: parseInt(hsm.contactCampaignDay) || 1
      });
      if (result.success) {
        toast.success('Contato atualizado com sucesso.');
        hsm.setContactDialogOpen(false);
        hsm.resetContactForm();
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleDeleteContact = useCallback(
    async (contactId: number) => {
      if (!confirm('Tem certeza que deseja excluir este contato?')) return;
      const result = await hsm.deleteContact(contactId);
      if (result.success) {
        toast.success('Contato excluído com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleActivateContact = useCallback(
    async (contactId: number) => {
      const result = await hsm.activateContact(contactId);
      if (result.success) {
        toast.success('Contato ativado com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleDeactivateContact = useCallback(
    async (contactId: number) => {
      const result = await hsm.deactivateContact(contactId);
      if (result.success) {
        toast.success('Contato desativado com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleCreateMessage = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await hsm.createCampaignMessage({
        day: parseInt(hsm.messageDay) || 1,
        templateName: hsm.messageTemplateName,
        languageCode: hsm.messageLanguageCode || 'pt_BR',
        templateParameters: hsm.messageTemplateParameters || undefined
      });
      if (result.success) {
        toast.success('Mensagem criada com sucesso.');
        hsm.setMessageDialogOpen(false);
        hsm.resetMessageForm();
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleUpdateMessage = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!hsm.selectedMessage) return;
      const result = await hsm.updateCampaignMessage(hsm.selectedMessage.id, {
        day: parseInt(hsm.messageDay) || 1,
        templateName: hsm.messageTemplateName,
        languageCode: hsm.messageLanguageCode || 'pt_BR',
        templateParameters: hsm.messageTemplateParameters || undefined
      });
      if (result.success) {
        toast.success('Mensagem atualizada com sucesso.');
        hsm.setMessageDialogOpen(false);
        hsm.resetMessageForm();
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleDeleteMessage = useCallback(
    async (messageId: number) => {
      if (!confirm('Tem certeza que deseja excluir esta mensagem?')) return;
      const result = await hsm.deleteCampaignMessage(messageId);
      if (result.success) {
        toast.success('Mensagem excluída com sucesso.');
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleRunCampaign = useCallback(async () => {
    if (!confirm('Tem certeza que deseja executar a campanha agora? Isso enviará mensagens para todos os contatos ativos.')) return;
    const result = await hsm.runCampaign();
    if (result.success) {
      toast.success(result.message || 'Campanha executada com sucesso!');
    } else {
      toast.error(result.error);
    }
  }, [hsm]);

  const handleRunCampaignDay = useCallback(
    async (day: number) => {
      if (!confirm(`Tem certeza que deseja executar o dia ${day} da campanha?`)) return;
      const result = await hsm.runCampaignDay(day);
      if (result.success) {
        toast.success(result.message || `Dia ${day} executado com sucesso!`);
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  const handleSendTestMessage = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // Convert comma-separated parameters string to array
      const parametersArray = hsm.testTemplateParameters
        ? hsm.testTemplateParameters.split(',').map((p) => p.trim()).filter((p) => p.length > 0)
        : undefined;
      const result = await hsm.sendTestMessage({
        phoneNumber: normalizePhoneE164(hsm.testPhoneNumber),
        templateName: hsm.testTemplateName,
        languageCode: hsm.testLanguageCode || 'en_US',
        parameters: parametersArray
      });
      if (result.success) {
        toast.success('Mensagem de teste enviada com sucesso!');
        hsm.setTestDialogOpen(false);
        hsm.resetTestForm();
      } else {
        toast.error(result.error);
      }
    },
    [hsm]
  );

  // BANT handlers
  const handleDeleteBantQualification = useCallback(
    async (chatId: string): Promise<{ success: boolean; error?: string }> => {
      const result = await bant.deleteQualification(chatId);
      if (result.success) {
        toast.success('Qualificacao BANT excluida com sucesso.');
      } else {
        toast.error(result.error);
      }
      return result;
    },
    [bant]
  );

  // Note: Helper functions for materials and users view are in:
  // - src/utils/material.helpers.tsx
  // - src/utils/user.helpers.tsx
  // These can be imported directly in the page components if needed.

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-100 via-slate-200 to-white dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#1a1a1a]">
      <header className="sticky top-0 z-30 border-b bg-white/80 backdrop-blur dark:bg-[#1a1a1a]/80">
        <div className="flex h-16 items-center gap-2 md:gap-3 px-3 md:px-4">
          <button
            type="button"
            onClick={navigateToHome}
            className="flex items-center gap-2 text-left transition hover:opacity-80 flex-shrink-0"
          >
            <img src={logoSymbol} alt="Mundo Transparente" className="h-6 w-6" />
            <img
              src={theme === 'light' ? logoWordmarkDark : logoWordmark}
              alt="Mundo Transparente"
              className="h-7 w-auto hidden xl:block"
            />
          </button>

          {isLoggedIn && user ? (
            <>
              {/* Mobile Menu Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={(open) => { setMobileMenuOpen(open); if (!open) setMobileSidebarSection(null); }}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] sm:w-[350px]">
                  <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                    <SheetDescription>
                      Navegue pelas funcionalidades do backoffice
                    </SheetDescription>
                  </SheetHeader>
                  <nav className="flex-1 py-2 overflow-y-auto mt-4">
                    {/* Main Menu (when no section is selected) */}
                    {mobileSidebarSection === null && (
                      <>
                        {/* Home */}
                        <button
                          type="button"
                          onClick={() => { navigateToHome(); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'home'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Home className="h-4 w-4" />
                          Início
                        </button>

                        {/* Usuários */}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => { navigateToUsers(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'users'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Users className="h-4 w-4" />
                            Usuários
                          </button>
                        )}

                        {/* HSM */}
                        {canManageHsm && (
                          <button
                            type="button"
                            onClick={() => { navigateToHsm(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'hsm'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <MessageSquare className="h-4 w-4" />
                            HSM
                          </button>
                        )}

                        {/* Vendas */}
                        {(canManageBant || canManageContacts) && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('vendas')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              (activeView === 'bant' || activeView === 'contacts')
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <ClipboardCheck className="h-4 w-4" />
                            Vendas
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Estoque */}
                        {canManageStorage && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('estoque')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'storage'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Package className="h-4 w-4" />
                            Estoque
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Relatórios */}
                        {canViewAnalytics && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('relatorios')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'analytics'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <BarChart3 className="h-4 w-4" />
                            Relatórios
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Blog */}
                        {canManageBlog && (
                          <button
                            type="button"
                            onClick={() => { setActiveView('blogger'); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'blogger'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Newspaper className="h-4 w-4" />
                            Blog
                          </button>
                        )}

                        {/* Monitor */}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => { setActiveView('monitor'); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'monitor'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Activity className="h-4 w-4" />
                            Monitor
                          </button>
                        )}

                        {/* VPS Dashboard */}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => { setActiveView('nginx-analytics'); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'nginx-analytics'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Server className="h-4 w-4" />
                            Dashboard VPS
                          </button>
                        )}

                        {/* Meta Targeting */}
                        {canManageMetaTargeting && (
                          <button
                            type="button"
                            onClick={() => { navigateToMetaTargeting(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'meta-targeting'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Target className="h-4 w-4" />
                            Meta Targeting
                          </button>
                        )}

                        {/* Google Trends */}
                        {canManageGoogleTrends && (
                          <button
                            type="button"
                            onClick={() => { navigateToGoogleTrends(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'google-trends'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <TrendingUp className="h-4 w-4" />
                            Tendências Google
                          </button>
                        )}

                        {/* Orçamentos Section Separator */}
                        {(canManageQuotes || canEdit || canManageMedals || canManageTrophies || canManageTombstones) && (
                          <div className="px-4 py-2 mt-2">
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                              Orçamentos
                            </div>
                          </div>
                        )}

                        {/* Chapas */}
                        {(canManageQuotes || canEdit) && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('orcamento')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              (activeView === 'materials' || activeView === 'quotes' || activeView === 'settings')
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <FolderKanban className="h-4 w-4" />
                            Chapas
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Medalhas */}
                        {canManageMedals && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('medalhas')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'medals'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Award className="h-4 w-4" />
                            Medalhas
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Troféus */}
                        {canManageTrophies && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('trofeus')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'trophies'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Trophy className="h-4 w-4" />
                            Troféus
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Tombstones */}
                        {canManageTombstones && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('tombstones')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'tombstones'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <TombstoneIcon />
                            Tombstones
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </button>
                        )}

                        {/* Placas de Premiacao */}
                        {canManagePlacas && (
                          <button
                            type="button"
                            onClick={() => setMobileSidebarSection('placas')}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'placas'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <PlacaIcon />
                            Placas
                            {notifications.placaUnreadCount > 0 && (
                              <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1 text-xs font-semibold text-primary">
                                {notifications.placaUnreadCount}
                              </span>
                            )}
                            {notifications.placaUnreadCount === 0 && (
                              <ChevronRight className="h-4 w-4 ml-auto" />
                            )}
                          </button>
                        )}

                        {/* Repescagem */}
                        {canManageRepescagem && (
                          <button
                            type="button"
                            onClick={() => { navigateToRepescagem(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'repescagem'
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Contact2 className="h-4 w-4" />
                            Repescagem
                          </button>
                        )}

                        {/* Conta Azul */}
                        {isAdmin && (
                        <button
                          type="button"
                          onClick={() => { setActiveView('conta-azul'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'conta-azul'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Plug className="h-4 w-4" />
                          Conta Azul
                        </button>
                        )}
                      </>
                    )}

                    {/* Chapas Section */}
                    {mobileSidebarSection === 'orcamento' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Chapas
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToMaterials(); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'materials'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FolderKanban className="h-4 w-4" />
                          Materiais
                        </button>
                        {canManageQuotes && (
                          <button
                            type="button"
                            onClick={() => { navigateToQuotes(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'quotes'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <FileDown className="h-4 w-4" />
                            Orçamentos
                            {notifications.unreadCount > 0 && (
                              <Badge className="ml-auto text-xs h-5 px-1.5 bg-blue-500/15 text-blue-400 border-blue-500/20">
                                {notifications.unreadCount}
                              </Badge>
                            )}
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => { navigateToSettings(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'settings'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Settings className="h-4 w-4" />
                            Configurações
                          </button>
                        )}
                      </>
                    )}

                    {/* Vendas Section */}
                    {mobileSidebarSection === 'vendas' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Vendas
                        </button>
                        {canManageBant && (
                          <button
                            type="button"
                            onClick={() => { navigateToBant(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'bant'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <ClipboardCheck className="h-4 w-4" />
                            BANT
                          </button>
                        )}
                        {canManageContacts && (
                          <button
                            type="button"
                            onClick={() => { navigateToContacts(); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                              activeView === 'contacts'
                                ? "bg-primary/10 text-primary border-r-2 border-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <Contact2 className="h-4 w-4" />
                            Contatos
                          </button>
                        )}
                      </>
                    )}

                    {/* Estoque Section */}
                    {mobileSidebarSection === 'estoque' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Estoque
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToStorageSection('entradas'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'storage' && storageSection === 'entradas'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FolderInput className="h-4 w-4" />
                          Entradas
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToStorageSection('inventario'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'storage' && storageSection === 'inventario'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Package className="h-4 w-4" />
                          Inventário
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToStorageSection('relatorios'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'storage' && storageSection === 'relatorios'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <BarChart3 className="h-4 w-4" />
                          Relatórios
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToStorageSection('configuracoes'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'storage' && storageSection === 'configuracoes'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Settings className="h-4 w-4" />
                          Configurações
                        </button>
                      </>
                    )}

                    {/* Medalhas Section */}
                    {mobileSidebarSection === 'medalhas' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Medalhas
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('medals'); setMedalsTab('products'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'medals' && medalsTab === 'products'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Package className="h-4 w-4" />
                          Produtos
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('medals'); setMedalsTab('categories'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'medals' && medalsTab === 'categories'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FolderKanban className="h-4 w-4" />
                          Categorias
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('medals'); setMedalsTab('materials'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'medals' && medalsTab === 'materials'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FolderInput className="h-4 w-4" />
                          Materiais
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('medals'); setMedalsTab('ribbons'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'medals' && medalsTab === 'ribbons'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Award className="h-4 w-4" />
                          Fitas
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('medals'); setMedalsTab('settings'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'medals' && medalsTab === 'settings'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Settings className="h-4 w-4" />
                          Configurações
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('medals'); setMedalsTab('quotes'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'medals' && medalsTab === 'quotes'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FileDown className="h-4 w-4" />
                          Orçamentos
                        </button>
                      </>
                    )}

                    {/* Troféus Section */}
                    {mobileSidebarSection === 'trofeus' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Troféus
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('trophies'); setTrophiesTab('products'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'trophies' && trophiesTab === 'products'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Package className="h-4 w-4" />
                          Produtos
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('trophies'); setTrophiesTab('quotes'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'trophies' && trophiesTab === 'quotes'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FileDown className="h-4 w-4" />
                          Orçamentos
                        </button>
                      </>
                    )}

                    {/* Tombstones Section */}
                    {mobileSidebarSection === 'tombstones' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Tombstones
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('tombstones'); setTombstonesTab('products'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'tombstones' && tombstonesTab === 'products'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Package className="h-4 w-4" />
                          Produtos
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('tombstones'); setTombstonesTab('quotes'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'tombstones' && tombstonesTab === 'quotes'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FileDown className="h-4 w-4" />
                          Orçamentos
                        </button>
                      </>
                    )}

                    {/* Placas Section */}
                    {mobileSidebarSection === 'placas' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Placas de Premiacao
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('placas'); setPlacasTab('products'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'placas' && placasTab === 'products'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Package className="h-4 w-4" />
                          Produtos
                        </button>
                        <button
                          type="button"
                          onClick={() => { setActiveView('placas'); setPlacasTab('quotes'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'placas' && placasTab === 'quotes'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FileDown className="h-4 w-4" />
                          Orcamentos
                          {notifications.placaUnreadCount > 0 && (
                            <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1 text-xs font-semibold text-primary">
                              {notifications.placaUnreadCount}
                            </span>
                          )}
                        </button>
                      </>
                    )}

                    {/* Relatórios Section */}
                    {mobileSidebarSection === 'relatorios' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileSidebarSection(null)}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Relatórios
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToAnalyticsSection('utm'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'analytics' && analyticsSection === 'utm'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <BarChart3 className="h-4 w-4" />
                          UTM
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToAnalyticsSection('chapas'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'analytics' && analyticsSection === 'chapas'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FileDown className="h-4 w-4" />
                          Chapas
                        </button>
                        <button
                          type="button"
                          onClick={() => { navigateToAnalyticsSection('medalhas'); setMobileMenuOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                            activeView === 'analytics' && analyticsSection === 'medalhas'
                              ? "bg-primary/10 text-primary border-r-2 border-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <Award className="h-4 w-4" />
                          Medalhas
                        </button>
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>

              {/* Desktop Navigation removed - now in sidebar */}

              {/* Centered notification counters */}
              {(notifications.hasQuoteAccess || notifications.hasMedalAccess || notifications.hasTrophyAccess || notifications.hasTombstoneAccess || notifications.hasPlacaAccess) && (
                <div className="flex-1 hidden md:flex justify-center">
                  <NotificationBadge
                    chapasCount={notifications.unreadCount}
                    medalhasCount={notifications.medalUnreadCount}
                    trofeusCount={notifications.trophyUnreadCount}
                    tombstonesCount={notifications.tombstoneUnreadCount}
                    placasCount={notifications.placaUnreadCount}
                    showChapas={notifications.hasQuoteAccess}
                    showMedalhas={notifications.hasMedalAccess}
                    showTrofeus={notifications.hasTrophyAccess}
                    showTombstones={notifications.hasTombstoneAccess}
                    showPlacas={notifications.hasPlacaAccess}
                    onChapasClick={() => {
                      setActiveView('quotes');
                      setMobileMenuOpen(false);
                    }}
                    onMedalhasClick={() => {
                      setActiveView('medals');
                      setMedalsTab('quotes');
                      setMobileMenuOpen(false);
                    }}
                    onTrofeusClick={() => {
                      setActiveView('trophies');
                      setTrophiesTab('quotes');
                      setMobileMenuOpen(false);
                    }}
                    onTombstonesClick={() => {
                      setActiveView('tombstones');
                      setTombstonesTab('quotes');
                      setMobileMenuOpen(false);
                    }}
                    onPlacasClick={() => {
                      setActiveView('placas');
                      setPlacasTab('quotes');
                      setMobileMenuOpen(false);
                    }}
                  />
                </div>
              )}

              <div className={cn("flex items-center gap-2 md:gap-4", !(notifications.hasQuoteAccess || notifications.hasMedalAccess || notifications.hasTrophyAccess || notifications.hasTombstoneAccess || notifications.hasPlacaAccess) && "ml-auto")}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 hidden sm:flex">
                      <User className="h-4 w-4" />
                      {user.name}
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        navigateToUserSettings();
                      }}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Configurações
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        handleLogout();
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {pwa.canShow && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => void pwa.install()}
                    title="Instalar aplicativo"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                )}
                {push.isSupported && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => void (push.isSubscribed ? push.unsubscribe() : push.subscribe())}
                    disabled={push.isLoading || push.permission === 'denied'}
                    title={
                      push.permission === 'denied'
                        ? 'Notificações bloqueadas no navegador'
                        : push.isSubscribed
                          ? 'Notificações ativas — clique para desativar'
                          : 'Ativar notificações push'
                    }
                    className={cn(
                      push.isSubscribed && 'text-primary',
                      push.permission === 'denied' && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    {push.isSubscribed ? (
                      <Bell className="h-4 w-4 fill-current" />
                    ) : (
                      <BellOff className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleTheme}
                  title={theme === 'light' ? 'Modo escuro' : theme === 'dark' ? 'Modo moss' : 'Modo claro'}
                >
                  {theme === 'light' ? <Moon className="h-4 w-4" /> : theme === 'dark' ? <Leaf className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </Button>
              </div>
            </>
          ) : (
            <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldQuestion className="h-4 w-4" />
              Acesso restrito
            </div>
          )}
        </div>
      </header>

      {!isLoggedIn ? (
        <main className="container flex flex-col gap-8 py-10">
          <LoginPage
            username={auth.username}
            setUsername={auth.setUsername}
            password={auth.password}
            setPassword={auth.setPassword}
            handleLogin={handleLogin}
          />
        </main>
      ) : (
        <div className="flex flex-1">
          {/* Left Sidebar - Desktop only */}
          <aside className="hidden md:flex w-56 flex-col border-r bg-muted/30">
            <nav className="flex-1 py-2 overflow-y-auto">
              {/* Main Menu (when no section is selected) */}
              {sidebarSection === null && (
                <>
                  {/* Home */}
                  <button
                    type="button"
                    onClick={navigateToHome}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'home'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Home className="h-4 w-4" />
                    Início
                  </button>

                  {/* Usuários */}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={navigateToUsers}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'users'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Users className="h-4 w-4" />
                      Usuários
                    </button>
                  )}

                  {/* HSM */}
                  {canManageHsm && (
                    <button
                      type="button"
                      onClick={navigateToHsm}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'hsm'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <MessageSquare className="h-4 w-4" />
                      HSM
                    </button>
                  )}

                  {/* Vendas */}
                  {(canManageBant || canManageContacts) && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('vendas')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        (activeView === 'bant' || activeView === 'contacts')
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      Vendas
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Estoque */}
                  {canManageStorage && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('estoque')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'storage'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Package className="h-4 w-4" />
                      Estoque
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Relatórios */}
                  {canViewAnalytics && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('relatorios')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'analytics'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <BarChart3 className="h-4 w-4" />
                      Relatórios
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Blog */}
                  {canManageBlog && (
                    <button
                      type="button"
                      onClick={() => { setActiveView('blogger'); setMobileMenuOpen(false); }}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'blogger'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Newspaper className="h-4 w-4" />
                      Blog
                    </button>
                  )}

                  {/* Monitor */}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setActiveView('monitor')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'monitor'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Activity className="h-4 w-4" />
                      Monitor
                    </button>
                  )}

                  {/* VPS Dashboard */}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setActiveView('nginx-analytics')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'nginx-analytics'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Server className="h-4 w-4" />
                      Dashboard VPS
                    </button>
                  )}

                  {/* Meta Targeting */}
                  {canManageMetaTargeting && (
                    <button
                      type="button"
                      onClick={navigateToMetaTargeting}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'meta-targeting'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Target className="h-4 w-4" />
                      Meta Targeting
                    </button>
                  )}

                  {/* Google Trends */}
                  {canManageGoogleTrends && (
                    <button
                      type="button"
                      onClick={navigateToGoogleTrends}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'google-trends'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <TrendingUp className="h-4 w-4" />
                      Tendências Google
                    </button>
                  )}

                  {/* Orçamentos Section Separator */}
                  {(canManageQuotes || canEdit || canManageMedals || canManageTrophies || canManageTombstones) && (
                    <div className="px-4 py-2 mt-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Orçamentos
                      </div>
                    </div>
                  )}

                  {/* Chapas (formerly Orçamento) */}
                  {(canManageQuotes || canEdit) && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('orcamento')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        (activeView === 'materials' || activeView === 'quotes' || activeView === 'settings')
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <FolderKanban className="h-4 w-4" />
                      Chapas
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Medalhas */}
                  {canManageMedals && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('medalhas')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'medals'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Award className="h-4 w-4" />
                      Medalhas
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Troféus */}
                  {canManageTrophies && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('trofeus')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'trophies'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Trophy className="h-4 w-4" />
                      Troféus
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Tombstones */}
                  {canManageTombstones && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('tombstones')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'tombstones'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <TombstoneIcon />
                      Tombstones
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </button>
                  )}

                  {/* Placas de Premiacao */}
                  {canManagePlacas && (
                    <button
                      type="button"
                      onClick={() => setSidebarSection('placas')}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'placas'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <PlacaIcon />
                      Placas
                      {notifications.placaUnreadCount > 0 ? (
                        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1 text-xs font-semibold text-primary">
                          {notifications.placaUnreadCount}
                        </span>
                      ) : (
                        <ChevronRight className="h-4 w-4 ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Repescagem */}
                  {canManageRepescagem && (
                    <button
                      type="button"
                      onClick={navigateToRepescagem}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'repescagem'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Contact2 className="h-4 w-4" />
                      Repescagem
                    </button>
                  )}

                  {/* Conta Azul */}
                  {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setActiveView('conta-azul')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'conta-azul'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Plug className="h-4 w-4" />
                    Conta Azul
                  </button>
                  )}
                </>
              )}

              {/* Chapas Section (formerly Orçamento) */}
              {sidebarSection === 'orcamento' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Chapas
                  </button>
                  <button
                    type="button"
                    onClick={navigateToMaterials}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'materials'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FolderKanban className="h-4 w-4" />
                    Materiais
                  </button>
                  {canManageQuotes && (
                    <button
                      type="button"
                      onClick={navigateToQuotes}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'quotes'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <FileDown className="h-4 w-4" />
                      Orçamentos
                      {notifications.unreadCount > 0 && (
                        <Badge className="ml-auto text-xs h-5 px-1.5 bg-blue-500/15 text-blue-400 border-blue-500/20">
                          {notifications.unreadCount}
                        </Badge>
                      )}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={navigateToSettings}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'settings'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Settings className="h-4 w-4" />
                      Configurações
                    </button>
                  )}
                </>
              )}

              {/* Vendas Section */}
              {sidebarSection === 'vendas' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Vendas
                  </button>
                  {canManageBant && (
                    <button
                      type="button"
                      onClick={navigateToBant}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'bant'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      BANT
                    </button>
                  )}
                  {canManageContacts && (
                    <button
                      type="button"
                      onClick={navigateToContacts}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        activeView === 'contacts'
                          ? "bg-primary/10 text-primary border-r-2 border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Contact2 className="h-4 w-4" />
                      Contatos
                    </button>
                  )}
                </>
              )}

              {/* Estoque Section */}
              {sidebarSection === 'estoque' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Estoque
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToStorageSection('entradas')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'storage' && storageSection === 'entradas'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FolderInput className="h-4 w-4" />
                    Entradas
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToStorageSection('inventario')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'storage' && storageSection === 'inventario'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Package className="h-4 w-4" />
                    Inventário
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToStorageSection('relatorios')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'storage' && storageSection === 'relatorios'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <BarChart3 className="h-4 w-4" />
                    Relatórios
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToStorageSection('configuracoes')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'storage' && storageSection === 'configuracoes'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Settings className="h-4 w-4" />
                    Configurações
                  </button>
                </>
              )}

              {/* Medalhas Section */}
              {sidebarSection === 'medalhas' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Medalhas
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('medals'); setMedalsTab('products'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'medals' && medalsTab === 'products'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Package className="h-4 w-4" />
                    Produtos
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('medals'); setMedalsTab('categories'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'medals' && medalsTab === 'categories'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FolderKanban className="h-4 w-4" />
                    Categorias
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('medals'); setMedalsTab('materials'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'medals' && medalsTab === 'materials'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FolderInput className="h-4 w-4" />
                    Materiais
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('medals'); setMedalsTab('ribbons'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'medals' && medalsTab === 'ribbons'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Award className="h-4 w-4" />
                    Fitas
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('medals'); setMedalsTab('settings'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'medals' && medalsTab === 'settings'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Settings className="h-4 w-4" />
                    Configurações
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('medals'); setMedalsTab('quotes'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'medals' && medalsTab === 'quotes'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FileDown className="h-4 w-4" />
                    Orçamentos
                  </button>
                </>
              )}

              {/* Troféus Section */}
              {sidebarSection === 'trofeus' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Troféus
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('trophies'); setTrophiesTab('products'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'trophies' && trophiesTab === 'products'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Package className="h-4 w-4" />
                    Produtos
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('trophies'); setTrophiesTab('quotes'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'trophies' && trophiesTab === 'quotes'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FileDown className="h-4 w-4" />
                    Orçamentos
                  </button>
                </>
              )}

              {/* Relatórios Section */}
              {sidebarSection === 'relatorios' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Relatórios
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToAnalyticsSection('utm')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'analytics' && analyticsSection === 'utm'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <BarChart3 className="h-4 w-4" />
                    UTM
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToAnalyticsSection('chapas')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'analytics' && analyticsSection === 'chapas'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FileDown className="h-4 w-4" />
                    Chapas
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToAnalyticsSection('medalhas')}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'analytics' && analyticsSection === 'medalhas'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Award className="h-4 w-4" />
                    Medalhas
                  </button>
                </>
              )}

              {/* Tombstones Section */}
              {sidebarSection === 'tombstones' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Tombstones
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('tombstones'); setTombstonesTab('products'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'tombstones' && tombstonesTab === 'products'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Package className="h-4 w-4" />
                    Produtos
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('tombstones'); setTombstonesTab('quotes'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'tombstones' && tombstonesTab === 'quotes'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FileDown className="h-4 w-4" />
                    Orçamentos
                  </button>
                </>
              )}

              {/* Placas Section */}
              {sidebarSection === 'placas' && (
                <>
                  <button
                    type="button"
                    onClick={() => setSidebarSection(null)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Placas de Premiacao
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('placas'); setPlacasTab('products'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'placas' && placasTab === 'products'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Package className="h-4 w-4" />
                    Produtos
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveView('placas'); setPlacasTab('quotes'); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                      activeView === 'placas' && placasTab === 'quotes'
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FileDown className="h-4 w-4" />
                    Orcamentos
                    {notifications.placaUnreadCount > 0 && (
                      <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1 text-xs font-semibold text-primary">
                        {notifications.placaUnreadCount}
                      </span>
                    )}
                  </button>
                </>
              )}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 overflow-auto p-6">
            <>

            {activeView === 'home' && (
              <HomePage
                navigateToQuotes={navigateToQuotes}
                navigateToMedalQuotes={() => { setActiveView('medals'); setMedalsTab('quotes'); }}
                navigateToTrophyQuotes={() => { setActiveView('trophies'); setTrophiesTab('quotes'); }}
                navigateToTombstoneQuotes={() => { setActiveView('tombstones'); setTombstonesTab('quotes'); }}
                navigateToPlacaQuotes={() => { setActiveView('placas'); setPlacasTab('quotes'); }}
                canManageQuotes={canManageQuotes}
                canManageMedals={canManageMedals}
                canManageTombstones={canManageTombstones}
                canManageTrophies={canManageTrophies}
                canManagePlacas={canManagePlacas}
                chapasUnreadCount={notifications.unreadCount}
                medalUnreadCount={notifications.medalUnreadCount}
                trophyUnreadCount={notifications.trophyUnreadCount}
                tombstoneUnreadCount={notifications.tombstoneUnreadCount}
                placaUnreadCount={notifications.placaUnreadCount}
              />
            )}

            {activeView === 'materials' && (
              <>
              <MaterialsPage
                filters={materials.filters}
                setFilters={materials.setFilters}
                loading={materials.loading}
                sortedMaterials={materials.sortedMaterials}
                materialsSort={materials.materialsSort}
                toggleMaterialsSort={materials.toggleMaterialsSort}
                toggleMaterialStatus={materials.toggleMaterialStatus}
                materialDialogOpen={materials.materialDialogOpen}
                setMaterialDialogOpen={materials.setMaterialDialogOpen}
                selectedMaterial={materials.selectedMaterial}
                materialCode={materials.materialCode}
                setMaterialCode={materials.setMaterialCode}
                materialName={materials.materialName}
                setMaterialName={materials.setMaterialName}
                materialDensity={materials.materialDensity}
                setMaterialDensity={materials.setMaterialDensity}
                materialPricePerKg={materials.materialPricePerKg}
                setMaterialPricePerKg={materials.setMaterialPricePerKg}
                materialActive={materials.materialActive}
                setMaterialActive={materials.setMaterialActive}
                materialAllThicknesses={materials.materialAllThicknesses}
                setMaterialAllThicknesses={materials.setMaterialAllThicknesses}
                materialThicknessInput={materials.materialThicknessInput}
                setMaterialThicknessInput={materials.setMaterialThicknessInput}
                materialDimensionLimits={materials.materialDimensionLimits}
                setMaterialDimensionLimits={materials.setMaterialDimensionLimits}
                materialSaving={materials.materialSaving}
                openMaterialDialog={materials.openMaterialDialog}
                refreshing={auth.refreshing}
                canEdit={canEdit}
                attemptRefresh={attemptRefresh}
                fetchMaterials={materials.fetchMaterials}
                handleCreateMaterial={handleCreateMaterial}
                handleUpdateMaterial={handleUpdateMaterial}
              />
              </>
            )}

            {activeView === 'users' && isAdmin && (
              <UsersPage
                userSearch={users.userSearch}
                setUserSearch={users.setUserSearch}
                usersLoading={users.usersLoading}
                sortedUsers={users.sortedUsers}
                usersSort={users.usersSort}
                toggleUsersSort={users.toggleUsersSort}
                currentUserId={user?.id}
                roleCatalog={users.roleCatalog}
                registerDialogOpen={registerDialogOpen}
                setRegisterDialogOpen={setRegisterDialogOpen}
                registerName={auth.registerName}
                setRegisterName={auth.setRegisterName}
                registerUsername={auth.registerUsername}
                setRegisterUsername={auth.setRegisterUsername}
                registerEmail={auth.registerEmail}
                setRegisterEmail={auth.setRegisterEmail}
                registerPassword={auth.registerPassword}
                setRegisterPassword={auth.setRegisterPassword}
                registerRoles={auth.registerRoles}
                toggleRole={auth.toggleRole}
                registerError={auth.registerError}
                registerSuccess={auth.registerSuccess}
                registerLoading={auth.registerLoading}
                selectedUser={users.selectedUser}
                openEditRolesDialog={users.openEditRolesDialog}
                openResetPasswordDialog={users.openResetPasswordDialog}
                openDeleteUserDialog={users.openDeleteUserDialog}
                editRolesDialogOpen={users.editRolesDialogOpen}
                setEditRolesDialogOpen={users.setEditRolesDialogOpen}
                editRoles={users.editRoles}
                setEditRoles={users.setEditRoles}
                editRolesLoading={users.editRolesLoading}
                resetPasswordDialogOpen={users.resetPasswordDialogOpen}
                setResetPasswordDialogOpen={users.setResetPasswordDialogOpen}
                newPassword={users.newPassword}
                setNewPassword={users.setNewPassword}
                resetPasswordLoading={users.resetPasswordLoading}
                deleteUserDialogOpen={users.deleteUserDialogOpen}
                setDeleteUserDialogOpen={users.setDeleteUserDialogOpen}
                deleteUserLoading={users.deleteUserLoading}
                fetchUsers={users.fetchUsers}
                handleRegisterSubmit={handleRegisterSubmit}
                handleEditRolesSubmit={handleEditRolesSubmit}
                handleResetPasswordSubmit={handleResetPasswordSubmit}
                handleToggleUserStatus={handleToggleUserStatus}
                handleDeleteUser={handleDeleteUser}
              />
            )}

            {activeView === 'settings' && canEdit && (
              <SettingsPage
                minimumOrderValue={settings.minimumOrderValue}
                minimumOrderInput={settings.minimumOrderInput}
                setMinimumOrderInput={settings.setMinimumOrderInput}
                settingsLoading={settings.settingsLoading}
                settingsSaving={settings.settingsSaving}
                pricingSettings={settings.pricingSettings}
                pricingInputs={settings.pricingInputs}
                setPricingInputs={settings.setPricingInputs}
                pricingLoading={settings.pricingLoading}
                pricingSaving={settings.pricingSaving}
                testCalc={settings.testCalc}
                setTestCalc={settings.setTestCalc}
                calculatePriceBreakdown={calculatePriceBreakdown}
                handleSaveMinimumOrder={handleSaveMinimumOrder}
                handleSavePricingSettings={handleSavePricingSettings}
              />
            )}

            {activeView === 'quotes' && canManageQuotes && (
              <>
                <QuotesPage
                  quotesSearch={quotes.quotesSearch}
                  setQuotesSearch={quotes.setQuotesSearch}
                  quotesLoading={quotes.quotesLoading}
                  quotesFilter={quotes.quotesFilter}
                  setQuotesFilter={quotes.setQuotesFilter}
                  quotesDateRange={quotes.quotesDateRange}
                  setQuotesDateRange={quotes.setQuotesDateRange}
                  allQuoteDates={quotes.allQuoteDates}
                  quotes={quotes.quotes}
                  selectedQuote={quotes.selectedQuote}
                  quoteDetailsOpen={quotes.quoteDetailsOpen}
                  setQuoteDetailsOpen={quotes.setQuoteDetailsOpen}
                  quoteEditMode={quotes.quoteEditMode}
                  isUpdatingQuote={quotes.isUpdatingQuote}
                  createQuoteDialogOpen={createQuoteDialogOpen}
                  setCreateQuoteDialogOpen={setCreateQuoteDialogOpen}
                  quotePdfDownloading={quotePdfDownloading}
                  quoteStatusUpdating={quoteStatusUpdating}
                  accessToken={accessToken}
                  fetchQuotes={fetchQuotes}
                  handleOpenQuoteDialog={handleOpenQuoteDialog}
                  handleDownloadQuotePDF={handleDownloadQuotePDF}
                  handleUpdateQuoteStatus={handleUpdateQuoteStatus}
                  handleEnterEditMode={quotes.enterEditMode}
                  handleCancelEdit={quotes.cancelEdit}
                  handleSaveQuoteChanges={handleSaveQuoteChanges}
                />
                <CreateQuoteDialog
                  open={createQuoteDialogOpen}
                  onOpenChange={setCreateQuoteDialogOpen}
                  onSuccess={async () => {
                    toast.success('Orçamento criado com sucesso!');
                    await fetchQuotes();
                  }}
                  accessToken={accessToken}
                />
              </>
            )}

            {activeView === 'user-settings' && (
              <UserSettingsPage
                hasOrcamentistaRole={notifications.hasQuoteAccess}
                notificationSettings={notifications.settings}
                updateNotificationSettings={notifications.updateSettings}
                theme={theme}
                setTheme={setTheme}
              />
            )}

            {activeView === 'hsm' && canManageHsm && (
              <HsmPage
                activeTab={hsm.activeTab}
                setActiveTab={hsm.setActiveTab}
                contacts={hsm.contacts}
                contactsLoading={hsm.contactsLoading}
                contactsFilter={hsm.contactsFilter}
                setContactsFilter={hsm.setContactsFilter}
                contactsSearch={hsm.contactsSearch}
                setContactsSearch={hsm.setContactsSearch}
                contactsSort={hsm.contactsSort}
                toggleContactsSort={hsm.toggleContactsSort}
                contactDialogOpen={hsm.contactDialogOpen}
                setContactDialogOpen={hsm.setContactDialogOpen}
                bulkImportDialogOpen={hsm.bulkImportDialogOpen}
                setBulkImportDialogOpen={hsm.setBulkImportDialogOpen}
                selectedContact={hsm.selectedContact}
                contactPhoneNumber={hsm.contactPhoneNumber}
                setContactPhoneNumber={hsm.setContactPhoneNumber}
                contactName={hsm.contactName}
                setContactName={hsm.setContactName}
                contactCompany={hsm.contactCompany}
                setContactCompany={hsm.setContactCompany}
                contactCpfCnpj={hsm.contactCpfCnpj}
                setContactCpfCnpj={hsm.setContactCpfCnpj}
                contactEmail={hsm.contactEmail}
                setContactEmail={hsm.setContactEmail}
                contactCampaignDay={hsm.contactCampaignDay}
                setContactCampaignDay={hsm.setContactCampaignDay}
                contactBatchId={hsm.contactBatchId}
                setContactBatchId={hsm.setContactBatchId}
                contactSaving={hsm.contactSaving}
                batches={hsm.batches}
                batchesLoading={hsm.batchesLoading}
                selectedBatchFilter={hsm.selectedBatchFilter}
                setSelectedBatchFilter={hsm.setSelectedBatchFilter}
                batchDialogOpen={hsm.batchDialogOpen}
                setBatchDialogOpen={hsm.setBatchDialogOpen}
                batchName={hsm.batchName}
                setBatchName={hsm.setBatchName}
                batchDescription={hsm.batchDescription}
                setBatchDescription={hsm.setBatchDescription}
                batchSaving={hsm.batchSaving}
                assignBatchDialogOpen={hsm.assignBatchDialogOpen}
                setAssignBatchDialogOpen={hsm.setAssignBatchDialogOpen}
                contactToAssign={hsm.contactToAssign}
                assignBatchId={hsm.assignBatchId}
                setAssignBatchId={hsm.setAssignBatchId}
                assigningBatch={hsm.assigningBatch}
                batchEditDialogOpen={hsm.batchEditDialogOpen}
                setBatchEditDialogOpen={hsm.setBatchEditDialogOpen}
                batchToEdit={hsm.batchToEdit}
                campaigns={hsm.campaigns}
                campaignsLoading={hsm.campaignsLoading}
                selectedCampaign={hsm.selectedCampaign}
                campaignDetailLoading={hsm.campaignDetailLoading}
                fetchCampaignsData={hsm.fetchCampaignsData}
                fetchCampaignDetail={hsm.fetchCampaignDetail}
                handleCreateCampaign={hsm.handleCreateCampaign}
                handleStartCampaign={hsm.handleStartCampaign}
                handlePauseCampaign={hsm.handlePauseCampaign}
                handleResumeCampaign={hsm.handleResumeCampaign}
                handleCompleteCampaign={hsm.handleCompleteCampaign}
                handleRestartCampaign={hsm.handleRestartCampaign}
                handleAddCampaignContacts={hsm.handleAddCampaignContacts}
                handleRunCampaignById={hsm.handleRunCampaign}
                handleDeleteCampaign={hsm.handleDeleteCampaign}
                clearSelectedCampaign={hsm.clearSelectedCampaign}
                campaignMessages={hsm.campaignMessages}
                messagesLoading={hsm.messagesLoading}
                selectedMessage={hsm.selectedMessage}
                messageDialogOpen={hsm.messageDialogOpen}
                setMessageDialogOpen={hsm.setMessageDialogOpen}
                messageDay={hsm.messageDay}
                setMessageDay={hsm.setMessageDay}
                messageTemplateName={hsm.messageTemplateName}
                setMessageTemplateName={hsm.setMessageTemplateName}
                messageLanguageCode={hsm.messageLanguageCode}
                setMessageLanguageCode={hsm.setMessageLanguageCode}
                messageTemplateParameters={hsm.messageTemplateParameters}
                setMessageTemplateParameters={hsm.setMessageTemplateParameters}
                messageVariableMapping={hsm.messageVariableMapping}
                setMessageVariableMapping={hsm.setMessageVariableMapping}
                messageSaving={hsm.messageSaving}
                messageLogs={hsm.messageLogs}
                logsLoading={hsm.logsLoading}
                campaignStats={hsm.campaignStats}
                statsLoading={hsm.statsLoading}
                testDialogOpen={hsm.testDialogOpen}
                setTestDialogOpen={hsm.setTestDialogOpen}
                testPhoneNumber={hsm.testPhoneNumber}
                setTestPhoneNumber={hsm.setTestPhoneNumber}
                testTemplateName={hsm.testTemplateName}
                setTestTemplateName={hsm.setTestTemplateName}
                testLanguageCode={hsm.testLanguageCode}
                setTestLanguageCode={hsm.setTestLanguageCode}
                testTemplateParameters={hsm.testTemplateParameters}
                setTestTemplateParameters={hsm.setTestTemplateParameters}
                testSending={hsm.testSending}
                isAdmin={isAdmin}
                fetchContacts={hsm.fetchContacts}
                openNewContactDialog={hsm.openNewContactDialog}
                openEditContactDialog={hsm.openEditContactDialog}
                handleCreateContact={handleCreateContact}
                handleUpdateContact={handleUpdateContact}
                handleDeleteContact={handleDeleteContact}
                handleActivateContact={handleActivateContact}
                handleDeactivateContact={handleDeactivateContact}
                fetchBatches={hsm.fetchBatches}
                handleCreateBatch={hsm.handleCreateBatch}
                handleDeleteBatch={hsm.handleDeleteBatch}
                openAssignBatchDialog={hsm.openAssignBatchDialog}
                handleAssignBatch={hsm.handleAssignBatch}
                openBatchEditDialog={hsm.openBatchEditDialog}
                handleUpdateBatch={hsm.handleUpdateBatch}
                handleCsvImport={hsm.handleCsvImport}
                fetchCampaignMessages={hsm.fetchCampaignMessages}
                openNewMessageDialog={hsm.openNewMessageDialog}
                openEditMessageDialog={hsm.openEditMessageDialog}
                handleCreateMessage={handleCreateMessage}
                handleUpdateMessage={handleUpdateMessage}
                handleDeleteMessage={handleDeleteMessage}
                fetchMessageLogs={hsm.fetchMessageLogs}
                fetchCampaignStats={hsm.fetchCampaignStats}
                handleRunCampaign={handleRunCampaign}
                handleRunCampaignDay={handleRunCampaignDay}
                openTestDialog={hsm.openTestDialog}
                handleSendTestMessage={handleSendTestMessage}
                sendTestMessage={hsm.sendTestMessage}
                resetTestForm={hsm.resetTestForm}
                templates={hsm.templates}
                templatesLoading={hsm.templatesLoading}
                templatesResponse={hsm.templatesResponse}
                approvedTemplates={hsm.approvedTemplates}
                templateCreating={hsm.templateCreating}
                fetchTemplates={hsm.fetchTemplates}
                handleCreateTemplate={hsm.createTemplate}
                handleUpdateTemplate={hsm.updateTemplate}
                handleDeleteTemplate={hsm.deleteTemplate}
                templateUpdating={hsm.templateUpdating}
                templateDeleting={hsm.templateDeleting}
                selectedTemplate={hsm.selectedTemplate}
                editTemplateDialogOpen={hsm.editTemplateDialogOpen}
                setEditTemplateDialogOpen={hsm.setEditTemplateDialogOpen}
                openEditTemplateDialog={hsm.openEditTemplateDialog}
                closeEditTemplateDialog={hsm.closeEditTemplateDialog}
                templateVariables={hsm.templateVariables}
                templateVariablesLoading={hsm.templateVariablesLoading}
                fetchTemplateVariables={hsm.fetchTemplateVariables}
                validateTemplateVariables={hsm.validateTemplateVariables}
                parseTemplateVariables={hsm.parseTemplateVariables}
                saveTemplateVariableMapping={hsm.saveTemplateVariableMapping}
                getTemplateVariableMapping={hsm.getTemplateVariableMapping}
              />
            )}

            {activeView === 'bant' && canManageBant && (
              <BantPage
                qualifications={bant.qualifications}
                loading={bant.loading}
                selectedQualification={bant.selectedQualification}
                detailsOpen={bant.detailsOpen}
                setDetailsOpen={bant.setDetailsOpen}
                deleteDialogOpen={bant.deleteDialogOpen}
                setDeleteDialogOpen={bant.setDeleteDialogOpen}
                deleting={bant.deleting}
                search={bant.search}
                setSearch={bant.setSearch}
                filter={bant.filter}
                setFilter={bant.setFilter}
                sort={bant.sort}
                toggleSort={bant.toggleSort}
                totalElements={bant.totalElements}
                fetchQualifications={bant.fetchQualifications}
                deleteQualification={handleDeleteBantQualification}
                openDetails={bant.openDetails}
                openDeleteDialog={bant.openDeleteDialog}
              />
            )}

            {activeView === 'contacts' && canManageContacts && (
              <ContactsPage
                contacts={contacts.contacts}
                loading={contacts.loading}
                selectedContact={contacts.selectedContact}
                detailsOpen={contacts.detailsOpen}
                setDetailsOpen={contacts.setDetailsOpen}
                detailsLoading={contacts.detailsLoading}
                search={contacts.search}
                setSearch={contacts.setSearch}
                filter={contacts.filter}
                setFilter={contacts.setFilter}
                sort={contacts.sort}
                toggleSort={contacts.toggleSort}
                totalElements={contacts.totalElements}
                fetchContacts={contacts.fetchContacts}
                openDetails={contacts.openDetails}
              />
            )}

            {activeView === 'storage' && canManageStorage && (
              <StoragePage
                accessToken={accessToken || ''}
                initialSection={storageSection}
              />
            )}

            {activeView === 'analytics' && canViewAnalytics && (
              <AnalyticsPage
                analyticsSection={analyticsSection}
                analytics={analytics.analytics}
                conversions={analytics.conversions}
                loading={analytics.loading}
                conversionsLoading={analytics.conversionsLoading}
                activeTab={analytics.activeTab}
                setActiveTab={analytics.setActiveTab}
                dateRange={analytics.dateRange}
                setDateRange={analytics.setDateRange}
                onRefresh={analytics.fetchAnalytics}
                onExportCampaigns={analytics.exportCampaignsCsv}
                onExportConversions={analytics.exportConversionsCsv}
                selectedEmail={analytics.selectedEmail}
                emailQuotes={analytics.emailQuotes}
                quotesLoading={analytics.quotesLoading}
                onRowClick={analytics.fetchQuotesForEmail}
                onCloseQuotes={analytics.closeQuotesModal}
                accessToken={accessToken || ''}
              />
            )}

            {activeView === 'monitor' && (
              isAdmin ? (
                <MonitorPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'nginx-analytics' && (
              isAdmin ? (
                <NginxAnalyticsPage accessToken={accessToken} />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'medals' && (
              canManageMedals ? (
                <MedalsPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                  activeTab={medalsTab}
                  setActiveTab={setMedalsTab}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'trophies' && (
              canManageTrophies ? (
                <TrophiesPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                  activeTab={trophiesTab}
                  setActiveTab={setTrophiesTab}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'tombstones' && (
              canManageTombstones ? (
                <TombstonesPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                  activeTab={tombstonesTab}
                  setActiveTab={setTombstonesTab}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'placas' && (
              canManagePlacas ? (
                <PlacasPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                  activeTab={placasTab}
                  setActiveTab={setPlacasTab}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'repescagem' && (
              canManageRepescagem ? (
                <RepescagemPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'meta-targeting' && (
              canManageMetaTargeting ? (
                <MetaTargetingPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                  isAdmin={isAdmin}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'google-trends' && (
              canManageGoogleTrends ? (
                <GoogleTrendsPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                  isAdmin={isAdmin}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'blogger' && (
              canManageBlog ? (
                <BlogPage
                  accessToken={accessToken}
                  refreshToken={refreshToken}
                  attemptRefresh={attemptRefresh}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Lock className="h-16 w-16 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
                  <p>Você não tem permissão para acessar esta seção.</p>
                  <p className="text-sm mt-2">Contate o administrador para obter acesso.</p>
                </div>
              )
            )}

            {activeView === 'conta-azul' && isAdmin && (
              <ContaAzulPage />
            )}
          </>
          </main>
        </div>
      )}

      {/* PWA Install Instructions Dialog */}
      <Dialog open={pwa.showInstructions} onOpenChange={pwa.setShowInstructions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pwa.getInstructions().title}</DialogTitle>
            <DialogDescription>
              Siga os passos abaixo para instalar o aplicativo:
            </DialogDescription>
          </DialogHeader>
          <ol className="list-decimal list-inside space-y-3 py-4">
            {pwa.getInstructions().steps.map((step, i) => (
              <li key={i} className="text-sm">{step}</li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
}
